import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { trigger, state, transition, style, animate } from '@angular/animations';
import { OtpInputComponent } from 'src/app/components/otp-input/otp-input.component';
import { AuthService, alertsAnimation, type MfaChallengeType } from 'src/app/auth.service';
import { AppComponent } from 'src/app/app.component';
import { Router, ActivatedRoute } from '@angular/router';
import { WebsocketService } from 'src/app/websocket.service';
import { UserPoolService } from 'src/app/userPool.service';
import { ThemingService } from 'src/app/theming.service';
import { ApiService } from 'src/app/api.service';
import { LambdaService } from 'src/app/lambda.service';


@Component({
  selector: 'app-sign-in',
  templateUrl: './sign-in.component.html',
  styleUrls: ['./sign-in.component.scss'],
  animations: [
    alertsAnimation,
    trigger('slideLeft', [
      state('void', style({ transform: 'translateX(0)' })),
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('0.5s ease-in-out'),
      ]),
    ]),
    trigger('fade', [
      state('void', style({ opacity: 0 })),
      transition(':enter, :leave', [animate('0s ease-in-out')]),
    ]),
  ],
  host: { '[@inOutAnimation]': '' },
})
export class SignInComponent implements OnInit, OnDestroy {
  readonly supportedPhoneCountries = [
    { code: 'CR', name: 'Costa Rica', prefix: '+506' },
    { code: 'SV', name: 'El Salvador', prefix: '+503' },
    { code: 'GT', name: 'Guatemala', prefix: '+502' },
  ] as const;

  exceptionMessage: string = '';
  isPasswordVisible: boolean = false;
  isConfirmPasswordVisible: boolean = false;
  rememberMe: boolean = false;
  needsNewPassword: boolean = false;
  userLoggin: any;
  currentStep: number = 1;
  state: boolean = false;
  shopId: number = 1;
  shopGuid: string = '';
  shops: any[] = [];
  mfaChallenge: MfaChallengeType | '' = '';
  verificationMode: 'MFA' | 'PHONE' = 'MFA';
  verificationCode: string = '';
  codeSent: boolean = false;
  phoneDestinationMasked: string = '';
  onboardingPhoneNumber: string = '';
  selectedPhoneCountryCode: 'CR' | 'SV' | 'GT' = 'CR';
  phoneRequiresInput: boolean = false;
  resendCooldown: number = 0;
  canResendCode: boolean = true;
  isMobydygSupportEmail = false;
  loading = false;
  isLoginSubmitting = false;
  isPasswordChanging = false;
  isOtpVerifying = false;
  isResendingCode = false;
  isOtpComplete: boolean = false;
  @ViewChild('otpInputRef') otpInputComponent!: OtpInputComponent;
  public signInForm!: FormGroup;
  public changePasswordForm!: FormGroup;
  private readonly resendCooldownSeconds = 30;
  private resendCooldownIntervalId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private appComponent: AppComponent,
    private authService: AuthService,
    private apiService: ApiService,
    private router: Router,
    private route: ActivatedRoute,
    private websocketService: WebsocketService,
    private userPoolService: UserPoolService,
    private themingService: ThemingService,
    private lambdaService: LambdaService
  ) {
    /* Creating a form group with two controls. */
    this.signInForm = this.fb.group({
      givenEmail: ['', [Validators.required, Validators.email]],
      givenShop: ['', [Validators.required]],
      givenPassword: ['', [Validators.required]],
      givenInsertedShop : ['', [Validators.required]],
      givenSelectedShop : ['', [Validators.required]],
    });

    this.changePasswordForm = this.fb.group(
      {
        newPassword: [
          '',
          [
            Validators.required,
            Validators.pattern(
              '^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?!.* ).{8,16}$'
            ),
          ],
        ],
        confirmNewPassword: ['', [Validators.required]],
      },
      {
        validators: this.authService.confirmPasswordValidator(
          'newPassword',
          'confirmNewPassword'
        ),
      }
    );
  }

  /**
   * It returns the controls of the signInForm.
   * @returns The signInForm.controls object.
   */
  get signUpFormValidation() {
    return this.signInForm.controls;
  }

  /**
   * It returns the form controls of the changePasswordForm
   * @returns The form controls.
   */
  get changePasswordFormValidation() {
    return this.changePasswordForm.controls;
  }

  get showResendCodeAction(): boolean {
    return (
      (this.verificationMode === 'PHONE' && this.codeSent) ||
      (this.verificationMode === 'MFA' && this.mfaChallenge === 'EMAIL_OTP')
    );
  }

  get resendButtonLabel(): string {
    if (this.isResendingCode) {
      return 'Reenviando...';
    }

    if (this.canResendCode) {
      return 'Reenviar código';
    }

    return `Reenviar en ${this.resendCooldown}s`;
  }

  get isResendButtonDisabled(): boolean {
    return !this.canResendCode || this.isOtpVerifying || this.isResendingCode;
  }

  onOtpChange(otp: string): void {
    this.verificationCode = otp;
    this.isOtpComplete = otp.length === 6;
  }

  private isSupportedLoginMfaChallenge(
    challenge: MfaChallengeType | string
  ): challenge is 'EMAIL_OTP' | 'SOFTWARE_TOKEN_MFA' {
    return challenge === 'EMAIL_OTP' || challenge === 'SOFTWARE_TOKEN_MFA';
  }

  private handleMfaRequiredResponse(
    response: { challenge: string; user: any },
    message = 'Se requiere autenticación multifactor (MFA)'
  ): boolean {
    if (!this.isSupportedLoginMfaChallenge(response.challenge)) {
      this.setLoginSubmissionLoading(false);
      this.currentStep = 1;
      this.mfaChallenge = '';
      this.verificationMode = 'MFA';
      this.resetResendCooldown();
      this.exceptionMessage = this.authService.handleErrors(
        new Error('SMS_MFA_LOGIN_NOT_ALLOWED'),
        this.signInForm.value.givenEmail
      );
      return false;
    }

    this.userLoggin = response.user;
    this.openMfaVerification(response.challenge, message);
    this.setLoginSubmissionLoading(false);
    return true;
  }

  private handleNewPasswordRequired(response: { user: any }): void {
    this.resetPhoneVerificationState();
    this.verificationMode = 'MFA';
    this.mfaChallenge = '';
    this.verificationCode = '';
    this.resetResendCooldown();
    this.currentStep = 1;
    this.needsNewPassword = true;
    this.userLoggin = response.user;
    this.exceptionMessage = '';
  }

  private resetPhoneVerificationState(): void {
    this.codeSent = false;
    this.phoneDestinationMasked = '';
    this.onboardingPhoneNumber = '';
    this.selectedPhoneCountryCode = this.detectDefaultPhoneCountryCode();
    this.phoneRequiresInput = false;
  }

  private detectDefaultPhoneCountryCode(): 'CR' | 'SV' | 'GT' {
    const locale = typeof navigator !== 'undefined' ? navigator.language : '';
    const normalizedLocale = (locale || '').toUpperCase().replace('_', '-');
    const region = normalizedLocale.split('-')[1] ?? '';

    if (region === 'SV') {
      return 'SV';
    }

    if (region === 'GT') {
      return 'GT';
    }

    if (region === 'CR') {
      return 'CR';
    }

    return 'CR';
  }

  private getSelectedPhoneCountryPrefix(): string {
    return (
      this.supportedPhoneCountries.find((country) => country.code === this.selectedPhoneCountryCode)?.prefix ??
      '+506'
    );
  }

  private sanitizeLocalPhoneNumber(phone: string): string {
    return phone.replace(/\D/g, '');
  }

  onLocalPhoneNumberChange(value: string): void {
    this.onboardingPhoneNumber = this.sanitizeLocalPhoneNumber(value);
    if (this.verificationMode === 'PHONE' && this.codeSent) {
      this.codeSent = false;
      this.verificationCode = '';
      this.isOtpComplete = false;
      this.phoneDestinationMasked = '';
      this.resetResendCooldown();
      if (this.otpInputComponent) {
        this.otpInputComponent.clear();
      }
    }
    this.exceptionMessage = '';
  }

  onPhoneCountryChange(): void {
    if (this.verificationMode === 'PHONE' && this.codeSent) {
      this.codeSent = false;
      this.verificationCode = '';
      this.isOtpComplete = false;
      this.phoneDestinationMasked = '';
      this.resetResendCooldown();
      if (this.otpInputComponent) {
        this.otpInputComponent.clear();
      }
    }
    this.exceptionMessage = '';
  }

  private getPhoneNumberForVerification(): string {
    const localNumber = this.sanitizeLocalPhoneNumber(this.onboardingPhoneNumber).trim();
    return `${this.getSelectedPhoneCountryPrefix()}${localNumber}`;
  }

  private hasValidLocalPhoneNumber(): boolean {
    return this.sanitizeLocalPhoneNumber(this.onboardingPhoneNumber).trim().length > 0;
  }

  private setLoginSubmissionLoading(isSubmitting: boolean): void {
    this.isLoginSubmitting = isSubmitting;
    this.loading = isSubmitting;
  }

  private clearResendCooldownTimer(): void {
    if (this.resendCooldownIntervalId !== null) {
      window.clearInterval(this.resendCooldownIntervalId);
      this.resendCooldownIntervalId = null;
    }
  }

  private resetResendCooldown(): void {
    this.clearResendCooldownTimer();
    this.resendCooldown = 0;
    this.canResendCode = true;
  }

  private startResendCooldown(seconds = this.resendCooldownSeconds): void {
    this.clearResendCooldownTimer();
    this.resendCooldown = seconds;
    this.canResendCode = false;

    this.resendCooldownIntervalId = window.setInterval(() => {
      if (this.resendCooldown <= 1) {
        this.resetResendCooldown();
        return;
      }

      this.resendCooldown -= 1;
    }, 1000);
  }

  private openMfaVerification(
    challenge: 'EMAIL_OTP' | 'SOFTWARE_TOKEN_MFA',
    message = 'Se requiere autenticación multifactor (MFA)'
  ): void {
    this.resetPhoneVerificationState();
    this.verificationMode = 'MFA';
    this.mfaChallenge = challenge;
    this.currentStep = 2;
    this.verificationCode = '';
    this.isOtpComplete = false;
    this.exceptionMessage = message;

    // Limpiar OTP Input component
    if (this.otpInputComponent) {
      this.otpInputComponent.clear();
    }

    if (challenge === 'EMAIL_OTP') {
      this.startResendCooldown();
      return;
    }

    this.resetResendCooldown();
  }

  private openPhoneVerification(hasPhoneNumber: boolean, otpSent = false, destinationMasked = ''): void {
    this.verificationMode = 'PHONE';
    this.mfaChallenge = '';
    this.currentStep = 2;
    this.verificationCode = '';
    this.isOtpComplete = false;
    this.phoneRequiresInput = !hasPhoneNumber;
    this.codeSent = otpSent;
    this.phoneDestinationMasked = destinationMasked;
    this.onboardingPhoneNumber = '';
    this.selectedPhoneCountryCode = this.detectDefaultPhoneCountryCode();
    this.exceptionMessage = '';

    // Limpiar OTP Input component
    if (this.otpInputComponent) {
      this.otpInputComponent.clear();
    }

    if (otpSent) {
      this.startResendCooldown();
      return;
    }

    this.resetResendCooldown();
  }

  private async completeAuthenticatedAccess(): Promise<void> {
    this.exceptionMessage = '';
    this.resetResendCooldown();

    if (this.shops.length === 1) {
      const [myShop] = this.shops;

      this.shopId = myShop.shopId;
      this.shopGuid = myShop.shopGuid;

      this.themingService.setShopId(this.shopId, this.shopGuid);

      await this.themingService.initialize();
      await this.websocketService.sendShopId();
      this.setLoginSubmissionLoading(false);
      this.router.navigate(['/home']);
      return;
    }

    this.setLoginSubmissionLoading(false);
    this.currentStep = 3;
  }

  async sendPhoneVerificationCode(): Promise<void> {
    if (this.isOtpVerifying || this.isResendingCode) {
      return;
    }

    try {
      this.exceptionMessage = '';

      const phoneToUse = this.phoneRequiresInput
        ? this.getPhoneNumberForVerification()
        : undefined;

      if (this.phoneRequiresInput && !this.hasValidLocalPhoneNumber()) {
        this.exceptionMessage = 'Ingresa un número de teléfono válido (solo dígitos).';
        return;
      }

      const result = await this.authService.startPhoneNumberVerification(phoneToUse);
      this.codeSent = true;
      this.phoneDestinationMasked = result.destinationMasked;
      this.verificationCode = '';
      this.isOtpComplete = false;
      this.exceptionMessage = '';
      this.startResendCooldown();
    } catch (error: any) {
      this.exceptionMessage = this.authService.handleErrors(error, this.signInForm.value.givenEmail);
    }
  }

  async resendVerificationCode(): Promise<void> {
    if (this.isResendButtonDisabled) {
      return;
    }

    this.isResendingCode = true;
    this.exceptionMessage = '';

    try {
      if (this.verificationMode === 'PHONE') {
        const phoneToUse = this.phoneRequiresInput
          ? this.getPhoneNumberForVerification()
          : undefined;

        if (this.phoneRequiresInput && !this.hasValidLocalPhoneNumber()) {
          this.exceptionMessage = 'Ingresa un número de teléfono válido (solo dígitos).';
          return;
        }

        const result = await this.authService.startPhoneNumberVerification(phoneToUse);
        this.codeSent = true;
        this.phoneDestinationMasked = result.destinationMasked;
        this.verificationCode = '';
        this.isOtpComplete = false;
        this.exceptionMessage = 'Te enviamos un nuevo código por SMS.';
        this.startResendCooldown();
        return;
      }

      if (this.mfaChallenge !== 'EMAIL_OTP') {
        return;
      }

      const result = await this.authService.resendSignInVerificationCode();

      if (result.status !== 'MFA_REQUIRED' || !result.challenge) {
        throw new Error('Invalid session for the user, the session is expired');
      }

      this.handleMfaRequiredResponse(
        {
          challenge: result.challenge,
          user: result.user,
        },
        result.challenge === 'EMAIL_OTP' ? 'Te enviamos un nuevo código a tu correo.' : ''
      );
    } catch (error: any) {
      this.exceptionMessage = this.authService.handleErrors(error, this.signInForm.value.givenEmail);
    } finally {
      this.isResendingCode = false;
    }
  }

  /**
   * The ngOnInit function checks for stored user and shop information and sets the form values
   * accordingly, as well as setting a rememberMe flag and retrieving the current step from session
   * storage.
   */
  ngOnInit(): void {
    // pool dev or prod | test
    this.shops = [];
    if (localStorage.getItem('userEmail') != null && sessionStorage.getItem('selectedEmail') != null) {
      this.signInForm.patchValue({
        givenEmail: localStorage.getItem('userEmail'),
      });
      localStorage.setItem('rememberMe', 'true');
    }

    if (sessionStorage.getItem('selectedEmail') != null) {
      this.signInForm.patchValue({
        givenShop: sessionStorage.getItem('selectedShop'),
        givenEmail: sessionStorage.getItem('selectedEmail'),
      });
    }
    this.rememberMe = localStorage.getItem('rememberMe') ? true : false;
    this.currentStep = Number(sessionStorage.getItem('selectedStep')) === 0 ? 1 : Number(sessionStorage.getItem('selectedStep'))

  }

  ngOnDestroy(): void {
    this.clearResendCooldownTimer();
  }


  /**
   * The function toggles the value of a boolean variable based on a checkbox input and removes a
   * localStorage item if the checkbox is unchecked.
   * @param {any} event - The event parameter is an object that represents the event that triggered the
   * onRememberMe function. It contains information about the event, such as the type of event, the
   * target element, and any additional data related to the event. In this case, the event is a
   * checkbox being checked or unchecked.
   */
  onRememberMe(event: any) {
      event.target.checked ? (this.rememberMe = true) : (this.rememberMe = false);
      if(!this.rememberMe){
        localStorage.removeItem('rememberMe');
      }
  }

  /**
   * It navigates to the recover-password component and passes the email value from the sign-in form as a
   * query parameter.
   */
  recoveryPassword() {
    this.router.navigate(['/recover-password']);
  }

  /**
   * It takes the email and password from the form, sends it to the authService, and if the user is
   * authenticated, it navigates to the my-shops page.
   */

  async verifyCode() {
    if (this.isOtpVerifying) {
      return;
    }

    if (this.verificationMode === 'PHONE') {
      if (!this.codeSent) {
        this.exceptionMessage = 'Primero debes solicitar el código de verificación.';
        return;
      }

      if (!this.verificationCode.trim()) {
        this.exceptionMessage = 'Ingresa el código de verificación.';
        return;
      }

      this.isOtpVerifying = true;
      try {
        const result = await this.authService.confirmPhoneNumberVerification(this.verificationCode.trim());
        this.exceptionMessage = '';

        if (result.status === 'MFA_REQUIRED') {
          this.openMfaVerification(result.challenge, 'Se requiere autenticación multifactor (MFA)');
          return;
        }

        await this.completeAuthenticatedAccess();
      } catch (error: any) {
        this.exceptionMessage = this.authService.handleErrors(error, this.signInForm.value.givenEmail);
      } finally {
        this.isOtpVerifying = false;
      }
      return;
    }

    if (!this.mfaChallenge) {
      this.exceptionMessage = 'No se detectó tipo de desafío MFA.';
      return;
    }

    if (!this.isSupportedLoginMfaChallenge(this.mfaChallenge)) {
      this.exceptionMessage = this.authService.handleErrors(
        new Error('SMS_MFA_LOGIN_NOT_ALLOWED'),
        this.signInForm.value.givenEmail
      );
      return;
    }

    this.isOtpVerifying = true;
    try {
      const result = await this.authService.confirmMfaCode(
        this.userLoggin,
        this.verificationCode,
        this.mfaChallenge
      );

      if (result.status === 'PHONE_VERIFICATION_REQUIRED') {
        this.openPhoneVerification(result.hasPhoneNumber ?? false);
        return;
      }

      await this.completeAuthenticatedAccess();
    } catch (error: any) {
      console.error("MFA Error:", error);
      this.exceptionMessage = this.authService.handleErrors(error, this.signInForm.value.givenEmail);
    } finally {
      this.isOtpVerifying = false;
    }
  }

  onSignIn() {
    if (this.isLoginSubmitting) {
      return;
    }

    sessionStorage.removeItem('selectedShop');
    sessionStorage.removeItem('selectedEmail');
    sessionStorage.removeItem('selectedStep');
    this.resetPhoneVerificationState();
    this.verificationMode = 'MFA';
    this.mfaChallenge = '';
    this.verificationCode = '';
    this.resetResendCooldown();
    this.setLoginSubmissionLoading(true);

    this.authService
      .onLogIn(
        this.signInForm.value.givenEmail,
        this.signInForm.value.givenPassword
      )
      .then((response) => {
        if (this.rememberMe) {
          localStorage.setItem('userEmail', this.signInForm.value.givenEmail);
        } else {
          localStorage.removeItem('userEmail');
        }

        if (
          response.status === 'NEW_PASSWORD_REQUIRED' &&
          'challengeName' in response &&
          response.challengeName === 'NEW_PASSWORD_REQUIRED'
        ) {
          this.handleNewPasswordRequired(response);
          return;
        }

        switch (response.status) {
          case 'MFA_REQUIRED':
            if (!response.challenge) {
              this.exceptionMessage = 'No se detectó tipo de desafío MFA.';
              break;
            }
            this.handleMfaRequiredResponse({
              challenge: response.challenge,
              user: response.user,
            });
            break;

          case 'PHONE_VERIFICATION_REQUIRED':
            this.needsNewPassword = false;
            this.openPhoneVerification(response.hasPhoneNumber);
            break;

          case 'SUCCESS':
            this.websocketService.sendShopId();
            this.router.navigate(['/home']);
            break;

          default:
            this.exceptionMessage = 'Ocurrió un error inesperado en el inicio de sesión.';
        }
      })
      .catch((error) => {
        this.exceptionMessage = this.authService.handleErrors(error, this.signInForm.value.givenEmail);
      })
      .finally(() => {
        this.setLoginSubmissionLoading(false);
      });
  }

  // New signin function
  signIn() {
    return this.authService
    .onLogIn(
      this.signInForm.value.givenEmail,
      this.signInForm.value.givenPassword
    )
    .then((response) => {
      if (this.rememberMe) {
        localStorage.setItem('userEmail', this.signInForm.value.givenEmail);
      } else {
        localStorage.removeItem('userEmail');
      }
      return response;

    })
    .catch((error) => {
      this.exceptionMessage = this.authService.handleErrors(error, this.signInForm.value.givenEmail);
      return null;
    });
  }



  async onChangePassword() {
    if (this.isPasswordChanging) {
      return;
    }

    this.isPasswordChanging = true;
    try {
      const result = await this.authService.onNewPasswordRequired(
        this.changePasswordForm.value.confirmNewPassword
      );

      if (result?.status === 'MFA_REQUIRED') {
        this.needsNewPassword = false;
        this.openMfaVerification(result.challenge, '');
        return;
      }

      if (result?.status === 'PHONE_VERIFICATION_REQUIRED') {
        this.needsNewPassword = false;
        this.openPhoneVerification(
          result.hasPhoneNumber,
          result.hasPhoneNumber,
          result.destinationMasked ?? ''
        );
        return;
      }

      if (result?.status === 'SUCCESS') {
        this.needsNewPassword = false;
        await this.completeAuthenticatedAccess();
      }
    } catch (error: any) {
      this.exceptionMessage = this.authService.handleErrors(error, this.signInForm.value.givenEmail);
    } finally {
      this.isPasswordChanging = false;
    }
  }


  /**
   * The function checks if the current step in a sign-up form is valid based on the presence of errors
   * in specific form fields.
   * @returns a boolean value indicating whether the current step in a sign-up form is valid or not.
   */
  isStepInvalid() {
    let stepValid = true;
    switch (this.currentStep){
      case 1:
        stepValid = this.signUpFormValidation['givenEmail'].errors ? true: false
        stepValid = this.signUpFormValidation['givenPassword'].errors ? true : false;
        break;
      case 2:
        stepValid = true;
        break;
      case 3:
        stepValid = this.signUpFormValidation['givenSelectedShop'].errors ? true : false;
        break;
      default: stepValid = true;
        break;
    }
    return stepValid;
  }

  /**
   * This function handles the next step in a multi-step form, with additional logic for production
   * environments.
   * @param {any} e - The parameter `e` is an event object, likely triggered by a user action such as a
   * button click or form submission.
   */
  async nextStep(e: any) {
    e.preventDefault();
    //e.target.disabled = true;
    this.exceptionMessage = "";

    if(this.currentStep === 1){
      if (this.isLoginSubmitting) {
        return;
      }
      this.setLoginSubmissionLoading(true);
      //get users from userpool based on the stage
      if(this.signInForm.value.givenEmail == "" || this.signInForm.value.givenPassword  == ""){
        this.setLoginSubmissionLoading(false);
        this.exceptionMessage = 'No se aceptan campos vacios.'
        return;
      }
      // if(this.signInForm.value.givenEmail == "soporte@mobydyg.com"){
      //   this.isMobydygSupportEmail = true;
      // }
      const shops = await this.userPoolService.getUserPoolShops(this.signInForm.value.givenEmail); // getUserBackofficePool
      if(!shops){
        e.target.disabled = false;
        this.setLoginSubmissionLoading(false);
        this.exceptionMessage = 'Este usuario no está registrado.'
        return;
      }
      this.shops = [];
      await this.lambdaService.getShopsByIds(shops)
        .then((data: any) => { // getShopsById
          this.shops = data;
        })
        .catch((error: any) => {
          this.setLoginSubmissionLoading(false);
          this.exceptionMessage = 'Error al obtener los datos de tus tiendas.'
        });
      //this.authService.initialize();
      this.resetPhoneVerificationState();
      this.verificationMode = 'MFA';
      this.mfaChallenge = '';
      this.verificationCode = '';
      this.resetResendCooldown();
      this.signIn().then((response: any) => {
        if(response == null){
          this.setLoginSubmissionLoading(false);
          return;
        }

        if (
          response.status === 'NEW_PASSWORD_REQUIRED' &&
          'challengeName' in response &&
          response.challengeName === 'NEW_PASSWORD_REQUIRED'
        ) {
          this.setLoginSubmissionLoading(false);
          this.handleNewPasswordRequired(response);
          return;
        }

        switch (response.status) {
          case 'MFA_REQUIRED':
            if (!response.challenge) {
              this.setLoginSubmissionLoading(false);
              this.exceptionMessage = 'No se detectó tipo de desafío MFA.';
              break;
            }
            this.handleMfaRequiredResponse({
              challenge: response.challenge,
              user: response.user,
            });
            break;

          case 'PHONE_VERIFICATION_REQUIRED':
            this.setLoginSubmissionLoading(false);
            this.needsNewPassword = false;
            this.openPhoneVerification(response.hasPhoneNumber);
            break;

          case 'SUCCESS':
            if(this.shops.length === 1){
              const [myShop] = this.shops;
              this.shopId = myShop.shopId;
              this.shopGuid = myShop.shopGuid;
              this.themingService.setShopId(this.shopId, this.shopGuid);
              this.themingService.initialize().then(()=>{
                this.setLoginSubmissionLoading(false);
                this.websocketService.sendShopId();
                this.router.navigate(['/home']);
              })
              return;
            }else{
              this.setLoginSubmissionLoading(false);
              this.currentStep = 3;
            }
            break;

          default:
            this.setLoginSubmissionLoading(false);
            this.exceptionMessage = 'Ocurrió un error inesperado en el inicio de sesión.';
        }
      });
    }

    if(this.currentStep == 2){
      this.verifyCode();
    }

    if(this.currentStep == 3){
      this.loading = true;
      let shopIdFinded = this.verifyInsertedShop(this.signInForm.value.givenSelectedShop);
      if(!shopIdFinded){
        this.exceptionMessage = 'El dominio no coincide con niguna tienda.'
        this.loading = false;
        return;
      }
      else{
        this.exceptionMessage = '';
        this.themingService.setShopId(shopIdFinded.shopId, shopIdFinded.shopGuid);
        //this.themingService.setShopId(this.shopId, this.shopGuid);
        await this.themingService.initialize().then(()=>{
          this.loading = false;
          this.websocketService.sendShopId();
          this.router.navigate(['/']);
        })
      }
    }

  }

  /**
   * This function handles going back to the previous step in a multi-step process.
   * @param {Event} e - The parameter "e" is an event object that is passed as an argument to the
   * function. It is used to prevent the default behavior of the event and to access properties and
   * methods related to the event.
   */
  async prevStep(e: Event) {
    e.preventDefault();
    if(this.currentStep !== 1){
      if (this.currentStep === 2) {
        try {
          // Canceling MFA/phone verification must also clear Cognito pending challenge state.
          await this.authService.onSignOut();
        } catch (error) {
          console.warn('Unable to sign out while canceling verification step.', error);
        }
        this.userLoggin = null;
      }

      this.currentStep = 1;
      this.verificationCode = '';
      this.mfaChallenge = '';
      this.verificationMode = 'MFA';
      this.resetPhoneVerificationState();
      this.resetResendCooldown();
      sessionStorage.removeItem('selectedStep');
      this.state = !this.state;
      this.exceptionMessage = '';
    }
  }

  /**
   * The function changes the value of the shopId property based on the selected option in a dropdown
   * menu.
   * @param {Event} event - Event is a built-in interface in TypeScript that represents an event that
   * occurs in the DOM (Document Object Model) of a web page. It contains information about the event,
   * such as the type of event, the target element that triggered the event, and any additional data
   * related to the event. In this
   */
  changeShop(event: Event){
    const element = event.target as HTMLSelectElement;
    this.signInForm.value.givenSelectedShop = element.value;
    //this.shopId = this.shops.find(item => item.shopId === parseInt(element.value)).shopId;
  }

  /**
   * Normaliza un dominio eliminando protocolos (http/https), www y rutas adicionales
   * @param domain El dominio a normalizar (puede incluir protocolo, www, etc.)
   * @returns El dominio normalizado (ej: "storetest.mobydyg.com")
   */
  normalizeDomain(domain: string): string {
    // Eliminar protocolo http(s)://
    let normalized = domain.replace(/^https?:\/\//i, '');
    // Eliminar www.
    normalized = normalized.replace(/^www\./i, '');
    // Eliminar cualquier barra o parámetros al final
    normalized = normalized.split('/', 1)[0];
    // Eliminar query params
    normalized = normalized.split('?', 1)[0];
    // Eliminar puerto si existe (opcional)
    normalized = normalized.split(':')[0];

    return normalized;
  }

  verifyInsertedShop(insertedShop: string){
    this.exceptionMessage = '';
    if(this.shops.find(shop => this.normalizeDomain(shop.shopDomain) === this.normalizeDomain(insertedShop)) === undefined){
      return null;
    }else{
      this.exceptionMessage = '';
      return this.shops.find(shop => this.normalizeDomain(shop.shopDomain) === this.normalizeDomain(insertedShop));
    }
  }

}
